import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { JOURNEY, getStage, whyForStage, TOTAL_STAGES } from "../lib/journey";

const ROUTE_STAGES = {
  "/library": ["5", "6"],
  "/books": ["6"],
  "/podcast": ["6"],
  "/events": ["6"],
};

const KEY = "adpl_journey";
const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};
const write = (data) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
};

const Ctx = createContext(null);
export const useJourney = () => useContext(Ctx);

export const JourneyProvider = ({ children }) => {
  const { pathname } = useLocation();

  const [state, setState] = useState(() => {
    const s = read();
    return {
      last: s.last || null,
      reached: s.reached || [],
      completed: s.completed || [],
      recommendedStart: s.recommendedStart || null,
      visited: Boolean(s.visited),
    };
  });
  const [returning] = useState(() => {
    const s = read();
    return Boolean(s.visited && (s.last || s.recommendedStart));
  });
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  const persist = useCallback((partial) => write({ ...read(), ...partial }), []);

  useEffect(() => {
    persist({ visited: true });
    setState((p) => ({ ...p, visited: true }));
  }, [persist]);

  // record progress as the user moves through the journey
  useEffect(() => {
    const stages = ROUTE_STAGES[pathname];
    if (!stages) return;
    setState((prev) => {
      const reached = Array.from(new Set([...prev.reached, ...stages]));
      const last = String(Math.max(...stages.map(Number)));
      persist({ reached, last, visited: true });
      return { ...prev, reached, last };
    });
  }, [pathname, persist]);

  const completeStage = useCallback((n) => {
    setState((prev) => {
      const completed = Array.from(new Set([...prev.completed, String(n)]));
      persist({ completed });
      return { ...prev, completed };
    });
  }, [persist]);

  const applyAssessment = useCallback((stageN) => {
    setState((prev) => {
      persist({ recommendedStart: String(stageN) });
      return { ...prev, recommendedStart: String(stageN) };
    });
  }, [persist]);

  const resetJourney = useCallback(() => {
    write({ visited: true });
    setState({ last: null, reached: [], completed: [], recommendedStart: null, visited: true });
  }, []);

  // ---- derived engine values ----
  const routeStages = ROUTE_STAGES[pathname] || [];
  const recStart = state.recommendedStart ? Number(state.recommendedStart) : null;
  const completedMax = state.completed.length ? Math.max(...state.completed.map(Number)) : 0;

  const currentN = Number(
    state.last || state.recommendedStart || (routeStages.length ? Math.max(...routeStages.map(Number)) : 1)
  );

  // a stage is unlocked if it is the recommended start, already reached, or the
  // step right after the last completed stage.
  const maxUnlocked = Math.max(1, recStart || 1, completedMax + 1, currentN);
  const isUnlocked = (n) => Number(n) <= maxUnlocked;

  // next recommended step: earliest unlocked, uncompleted stage (respecting the
  // recommended starting point from the assessment).
  const floor = recStart || 1;
  let nextN = Math.min(floor, TOTAL_STAGES);
  for (let n = floor; n <= TOTAL_STAGES; n++) {
    if (!state.completed.includes(String(n))) {
      nextN = n;
      break;
    }
    nextN = Math.min(n + 1, TOTAL_STAGES);
  }
  if (nextN > maxUnlocked) nextN = maxUnlocked;

  const value = {
    stages: JOURNEY,
    reached: state.reached,
    completed: state.completed,
    recommendedStart: recStart,
    returning,
    currentStage: getStage(currentN),
    nextStage: getStage(nextN),
    whyNext: whyForStage(nextN),
    isUnlocked,
    completeStage,
    applyAssessment,
    resetJourney,
    assessmentOpen,
    openAssessment: () => setAssessmentOpen(true),
    closeAssessment: () => setAssessmentOpen(false),
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
